import { Admin, Error, Layout, LoginPage } from '@/components/admin';
import { authProvider } from './authProvider';
import { auth } from './lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { Route } from 'react-router';
import { CompleteProfilePage } from './customRoutes/authentication/CompleteProfilePage';
import AuthPageWrapper from './customRoutes/authentication/components/AuthPageWrapper';
import NotFound from './NotFound';
import { QueryClient } from '@tanstack/react-query';
import { getDataProvider } from './dataProvider/dataProvider';
import { lazy, useEffect, useState } from 'react';
import { i18nProvider } from './i18nProvider';
import { AdvertiserLayout } from './layout/advertiserLayout/AdvertiserLayout';
import NavigateAdvertiserIdPrefix from './components/NavigateAdvertiserIdPrefix';
import ForgotPasswordPage from './customRoutes/authentication/ForgotPasswordPage';
import AuthActionPage from './customRoutes/authentication/action/AuthActionPage';
import { Authenticated, CustomRoutes, Resource } from 'ra-core';
import { ChartColumnIncreasing } from 'lucide-react';
import { LoginForm } from '@/components/ui/login-form.tsx';

// Route chunk loaders are named so they can be preloaded (see the idle
// prefetch in App below). Preloading these matters for the page transition:
// PageTransition holds the new page at opacity 0 until its data loads, then
// fades the loaded content in. A cold lazy() chunk suspends BEFORE any data
// query runs, so the fade would race ahead of a Suspense fallback and the
// real content would pop in. Warming the chunks ahead of navigation keeps the
// fade carrying fully-rendered content.
const loadCampaignList = () =>
  import('./resources/campaigns/list/CampaignList').then(m => ({
    default: m.CampaignList,
  }));
const loadCampaignDetails = () =>
  import('@/resources/campaigns/CampaignDetails.tsx');
const loadCampaignOnboarding = () =>
  import('@/resources/campaigns/CampaignOnboarding.tsx');
const loadReports = () => import('@/resources/reports/Reports.tsx');

const CampaignList = lazy(loadCampaignList);
const CampaignDetails = lazy(loadCampaignDetails);
const CampaignOnboarding = lazy(loadCampaignOnboarding);
const Reports = lazy(loadReports);

/** Warm the campaign route chunks during idle time so first navigation into a
 * detail/onboarding page doesn't trigger a Suspense fallback mid-transition. */
function prefetchRouteChunks() {
  const run = () => {
    loadCampaignDetails();
    loadCampaignOnboarding();
    loadReports();
  };
  if ('requestIdleCallback' in window) {
    (window as Window & typeof globalThis).requestIdleCallback(run);
  } else {
    setTimeout(run, 1200);
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, gcTime: 0 },
  },
});

export const App = () => {
  const [dataProvider, setDataProvider] = useState<any>();
  const [ready, setReady] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    getDataProvider().then(d => {
      setDataProvider(d);
    });
    authProvider.setBrowserLocalPersistence().then(() => setReady(true));
    prefetchRouteChunks();
    const unsubscribe = onAuthStateChanged(auth, () => {
      setAuthReady(true);
      unsubscribe();
    });
  }, []);

  if (!dataProvider) return null;
  if (!ready) return null;
  if (!authReady) return null;

  return (
    <Admin
      requireAuth={false}
      queryClient={queryClient}
      dataProvider={dataProvider}
      authProvider={authProvider}
      loginPage={LoginPage}
      // dashboard={Dashboard}
      layout={Layout}
      i18nProvider={i18nProvider}
      catchAll={NotFound}
      error={Error}
    >
      <Resource name="campaigns" list={CampaignList} />
      <Resource
        name="reports"
        list={Reports}
        icon={ChartColumnIncreasing}
        options={{ label: 'Reporting' }}
      />
      <CustomRoutes>
        <Route
          path="/campaigns/*"
          element={
            <Authenticated>
              <NavigateAdvertiserIdPrefix replace />
            </Authenticated>
          }
        />
        <Route
          path="/advertiser/:advertiserId"
          element={
            <Authenticated>
              <AdvertiserLayout />
            </Authenticated>
          }
        >
          {/*Campaigns*/}
          <Route path="" element={<NavigateAdvertiserIdPrefix replace />} />
          <Route path="campaigns/:status" element={<CampaignList />} />
          <Route
            path="campaigns"
            element={<NavigateAdvertiserIdPrefix replace />}
          />
          <Route path="campaign/:campaignId" element={<CampaignDetails />} />
          <Route
            path="campaign/:campaignId/onboarding"
            element={<CampaignOnboarding />}
          />
          <Route
            path="campaign/:campaignId/onboarding/creative/:creativeId"
            element={<CampaignOnboarding />}
          />
        </Route>
      </CustomRoutes>

      {/* Auth Routes */}
      <CustomRoutes noLayout>
        <Route
          path="/signup"
          element={
            <AuthPageWrapper>
              <LoginForm type="signup" />
            </AuthPageWrapper>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <AuthPageWrapper>
              <ForgotPasswordPage />
            </AuthPageWrapper>
          }
        />
        <Route
          path="auth/action"
          element={
            <AuthPageWrapper>
              <AuthActionPage />
            </AuthPageWrapper>
          }
        />
        <Route
          path="/complete-profile"
          element={
            <Authenticated>
              <AuthPageWrapper>
                <CompleteProfilePage />
              </AuthPageWrapper>
            </Authenticated>
          }
        />
        {/*<Route*/}
        {/*  path="/profile-confirmation"*/}
        {/*  element={*/}
        {/*    <Authenticated>*/}
        {/*      <AuthPageWrapper>*/}
        {/*        <ProfileConfirmationPage />*/}
        {/*      </AuthPageWrapper>*/}
        {/*    </Authenticated>*/}
        {/*  }*/}
        {/*/>*/}
      </CustomRoutes>
    </Admin>
  );
};
